<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import DatePicker from 'primevue/datepicker';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';

const props = defineProps<{
  search: string;
  carrier: string[];
  carrierOptions: string[];
  pickupFromDate: Date | null;
  pickupToDate: Date | null;
  totalCount: number;
  viewMode: 'table' | 'map';
  canManage: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:search', value: string): void;
  (e: 'update:carrier', value: string[]): void;
  (e: 'update:pickupFromDate', value: Date | null): void;
  (e: 'update:pickupToDate', value: Date | null): void;
  (e: 'update:viewMode', value: 'table' | 'map'): void;
  (e: 'clear'): void;
}>();

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const searchInput = ref(props.search ?? '');
let debounceTimer: ReturnType<typeof setTimeout> | undefined;

watch(searchInput, (val) => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    emit('update:search', val);
  }, 300);
});

watch(
  () => props.search,
  (val) => {
    if (!val && searchInput.value) searchInput.value = '';
  },
);

const selectedCarriers = computed({
  get: () => props.carrier ?? [],
  set: (val) => emit('update:carrier', val),
});

const pickupDateRange = computed({
  get: () => {
    const from = props.pickupFromDate ?? null;
    const to = props.pickupToDate ?? null;
    if (!from && !to) return null;
    return [from, to] as [Date | null, Date | null];
  },
  set: (val: [Date | null, Date | null] | null) => {
    if (!val || (!val[0] && !val[1])) {
      emit('update:pickupFromDate', null);
      emit('update:pickupToDate', null);
    } else {
      emit('update:pickupFromDate', val[0]);
      emit('update:pickupToDate', val[1]);
    }
  },
});

function clearAll() {
  searchInput.value = '';
  emit('clear');
}

const hasActiveFilters = computed(() => {
  return !!(
    props.search ||
    props.carrier?.length ||
    props.pickupFromDate ||
    props.pickupToDate
  );
});

const filterChips = computed(() => {
  const chips: { key: string; label: string; onRemove: () => void }[] = [];

  props.carrier?.forEach((c) => {
    chips.push({
      key: `carrier-${c}`,
      label: `Carrier: ${c}`,
      onRemove: () =>
        emit(
          'update:carrier',
          (props.carrier ?? []).filter((x) => x !== c),
        ),
    });
  });

  if (props.pickupFromDate) {
    chips.push({
      key: 'pickupFromDate',
      label: `Pickup from: ${fmtDate(props.pickupFromDate)}`,
      onRemove: () => emit('update:pickupFromDate', null),
    });
  }

  if (props.pickupToDate) {
    chips.push({
      key: 'pickupToDate',
      label: `Pickup to: ${fmtDate(props.pickupToDate)}`,
      onRemove: () => emit('update:pickupToDate', null),
    });
  }

  return chips;
});
</script>

<template>
  <div class="space-y-2">
    <div class="flex items-center gap-3">
      <!-- Total count -->
      <div
        class="w-20 shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-gray-500"
      >
        Total: {{ totalCount }}
      </div>

      <!-- Search -->
      <IconField class="w-64 shrink-0">
        <InputIcon class="pi pi-search" />
        <InputText
          v-model="searchInput"
          placeholder="Search by reference ID or device serial..."
          class="w-full"
          size="small"
        />
      </IconField>

      <!-- Carrier -->
      <MultiSelect
        v-model="selectedCarriers"
        :options="carrierOptions"
        placeholder="All Carriers"
        :maxSelectedLabels="0"
        :selectedItemsLabel="`{0} Carrier(s)`"
        :showToggleAll="false"
        size="small"
        class="w-44"
      />

      <!-- Pickup Date -->
      <DatePicker
        v-model="pickupDateRange"
        selectionMode="range"
        placeholder="Pickup Date"
        dateFormat="yy-mm-dd"
        showIcon
        iconDisplay="input"
        size="small"
        showButtonBar
        class="w-64"
      />

      <!-- Clear all -->
      <button
        v-if="hasActiveFilters"
        class="whitespace-nowrap text-sm font-medium text-gray-500 hover:text-gray-700"
        @click="clearAll"
      >
        Clear
      </button>

      <div class="flex-1"></div>

      <!-- Table / Map toggle -->
      <div
        class="inline-flex shrink-0 rounded-lg border border-gray-300 bg-white p-0.5"
      >
        <button
          class="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
          :class="
            viewMode === 'table'
              ? 'bg-gpx_terracotta-100 text-white shadow-sm'
              : 'text-gray-500 hover:bg-gray-50'
          "
          @click="emit('update:viewMode', 'table')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="size-3.5"
          >
            <path
              fill-rule="evenodd"
              d="M.99 5.24A2.25 2.25 0 0 1 3.25 3h13.5A2.25 2.25 0 0 1 19 5.25l.01 9.5A2.25 2.25 0 0 1 16.76 17H3.26A2.267 2.267 0 0 1 1 14.74l-.01-9.5Zm8.26 9.52v-.625a.75.75 0 0 0-.75-.75H3.25a.75.75 0 0 0-.75.75v.615c0 .414.336.75.75.75h5.373a.75.75 0 0 0 .627-.74Zm1.5 0a.75.75 0 0 0 .627.74h5.373a.75.75 0 0 0 .75-.75v-.615a.75.75 0 0 0-.75-.75H11.5a.75.75 0 0 0-.75.75v.625Zm6.75-3.63v-.625a.75.75 0 0 0-.75-.75H11.5a.75.75 0 0 0-.75.75v.625c0 .414.336.75.75.75h5.25a.75.75 0 0 0 .75-.75Zm-8.25 0v-.625a.75.75 0 0 0-.75-.75H3.25a.75.75 0 0 0-.75.75v.625c0 .414.336.75.75.75H8.5a.75.75 0 0 0 .75-.75ZM17.5 7.5v-.625a.75.75 0 0 0-.75-.75H11.5a.75.75 0 0 0-.75.75V7.5c0 .414.336.75.75.75h5.25a.75.75 0 0 0 .75-.75Zm-8.25 0v-.625a.75.75 0 0 0-.75-.75H3.25a.75.75 0 0 0-.75.75V7.5c0 .414.336.75.75.75H8.5a.75.75 0 0 0 .75-.75Z"
              clip-rule="evenodd"
            />
          </svg>
          Table
        </button>
        <button
          class="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
          :class="
            viewMode === 'map'
              ? 'bg-gpx_terracotta-100 text-white shadow-sm'
              : 'text-gray-500 hover:bg-gray-50'
          "
          @click="emit('update:viewMode', 'map')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="size-3.5"
          >
            <path
              fill-rule="evenodd"
              d="M8.157 2.176a1.5 1.5 0 0 0-1.147 0l-4.084 1.69A1.5 1.5 0 0 0 2 5.25v10.877a1.5 1.5 0 0 0 2.074 1.386l3.51-1.452 4.26 1.762a1.5 1.5 0 0 0 1.146 0l4.083-1.69A1.5 1.5 0 0 0 18 14.75V3.872a1.5 1.5 0 0 0-2.073-1.386l-3.51 1.452-4.26-1.762ZM7.58 5a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-6.5A.75.75 0 0 1 7.58 5Zm5.59 2.75a.75.75 0 0 0-1.5 0v6.5a.75.75 0 0 0 1.5 0v-6.5Z"
              clip-rule="evenodd"
            />
          </svg>
          Map
        </button>
      </div>

      <router-link
        v-if="canManage"
        :to="{ name: 'ShipmentCreateView' }"
        class="inline-flex shrink-0 items-center gap-1 rounded-md bg-gpx_terracotta-100 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gpx_terracotta-200"
      >
        <span class="text-sm leading-none">+</span>
        Create Shipment
      </router-link>
    </div>

    <!-- Active filter chips -->
    <div
      v-if="filterChips.length > 0"
      class="flex flex-wrap items-center gap-1.5"
    >
      <span
        v-for="chip in filterChips"
        :key="chip.key"
        class="inline-flex items-center gap-1 rounded-full bg-gray-100 py-1 pl-2.5 pr-1 text-xs font-normal text-gray-700"
      >
        {{ chip.label }}
        <button
          class="ml-0.5 rounded-full p-0.5 hover:bg-gray-200"
          @click="chip.onRemove()"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="size-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </span>
      <button
        class="ml-1 text-xs text-gray-500 underline hover:text-gray-700"
        @click="clearAll"
      >
        Clear all filters
      </button>
    </div>
  </div>
</template>
